import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Backfills the gradient-fill fields on every box and circle graphic. These fields were added so
 * box/circle graphics can be filled with a linear gradient instead of a solid colour:
 *   - `fillType` ('solid' | 'linear')
 *   - `gradientStartPoint`, `gradientEndPoint` — each `{ x, y, color, alpha }`, where x/y are
 *     normalized 0..1 coordinates and color/alpha are the gradient stop at that point.
 *
 * Every graphic that exists today predates the feature, so they are all backfilled with the schema
 * defaults (a solid fill, with the points holding their default red→blue horizontal gradient).
 *
 * Box/circle graphics live in two shapes in the data:
 *   - text-asset graphics: `{normalStyle,highlightStyle,spokenWordStyle}.graphics[]`, discriminated
 *     by the graphic's `type` ('box' | 'circle')
 *   - scene-design graphics: `{colorGraphics,alphaGraphics}[].shape`, discriminated by the
 *     configurator's `graphic` field ('box' | 'circle')
 *
 * `textPositionPresets` store assets saved with `skipTemplateAttributes`. Because each point's
 * `color`/`alpha` are template (Colour/Alpha) attributes, a freshly-saved preset point carries only
 * `x`/`y`. Preset graphics are therefore backfilled with `x`/`y`-only points, so migrated data
 * matches freshly-saved data and applying a preset never overwrites gradient colours.
 *
 * `migrateScene` is self-contained so external callers using `Migrator.updateSceneData` /
 * `updateSceneDesignData` get the same result. SceneGroup caption assets are migrated in a private
 * `migrateSceneGroupCaptions` helper called from `migrateVideo`.
 */

const GRADIENT_GRAPHIC_TYPES = ['box', 'circle'];

const STYLE_FIELDS = ['normalStyle', 'highlightStyle', 'spokenWordStyle'] as const;

const COLOR_GRAPHIC_FIELDS = ['colorGraphics', 'alphaGraphics'] as const;

function backfillGraphic(
    map: Immutable.Map<string, unknown>,
    graphicPath: Array<string | number>,
    includeTemplateFields: boolean,
): void {
    // fillType is the first field added; if it's present this graphic is already migrated.
    if (map.getIn([...graphicPath, 'fillType']) !== undefined) {
        return;
    }
    map.setIn([...graphicPath, 'fillType'], 'solid');
    // The colour and alpha live inside each point; presets are saved with skipTemplateAttributes,
    // so their points carry only x/y (the Colour/Alpha fields are stripped).
    const startPoint: Record<string, number> = { x: 0, y: 0.5 };
    const endPoint: Record<string, number> = { x: 1, y: 0.5 };
    if (includeTemplateFields) {
        startPoint.color = 0xff0000;
        startPoint.alpha = 1;
        endPoint.color = 0x0000ff;
        endPoint.alpha = 1;
    }
    map.setIn([...graphicPath, 'gradientStartPoint'], startPoint);
    map.setIn([...graphicPath, 'gradientEndPoint'], endPoint);
}

function backfillAsset(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
    includeTemplateFields: boolean,
): void {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) return;

    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;
        const graphics = style.get('graphics') as
            | Immutable.List<Immutable.Map<string, unknown>>
            | undefined;
        if (!graphics) return;
        graphics.forEach((graphic, gIndex) => {
            if (GRADIENT_GRAPHIC_TYPES.includes(graphic.get('type') as string)) {
                backfillGraphic(
                    map,
                    [...assetPath, styleField, 'graphics', gIndex],
                    includeTemplateFields,
                );
            }
        });
    });

    COLOR_GRAPHIC_FIELDS.forEach((graphicField) => {
        const configurators = asset.get(graphicField) as
            | Immutable.List<Immutable.Map<string, unknown>>
            | undefined;
        if (!configurators) return;
        configurators.forEach((configurator, gIndex) => {
            if (GRADIENT_GRAPHIC_TYPES.includes(configurator.get('graphic') as string)) {
                backfillGraphic(
                    map,
                    [...assetPath, graphicField, gIndex, 'shape'],
                    includeTemplateFields,
                );
            }
        });
    });
}

function backfillChildren(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
    includeTemplateFields: boolean,
): void {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) return;
    children.forEach((_child, i) => {
        backfillAsset(map, [...childrenPath, i], includeTemplateFields);
    });
}

/**
 * Backfills a sceneGroup's caption asset and decorations. Called from `migrateVideo` because
 * sceneGroup data has no external migration entrypoint.
 */
function migrateSceneGroupCaptions(
    sceneGroup: Immutable.Map<string, unknown>,
): Immutable.Map<string, unknown> {
    return sceneGroup.withMutations((map) => {
        if (map.getIn(['captions', 'asset'])) {
            backfillAsset(map, ['captions', 'asset'], true);
        }
        if (map.getIn(['captions', 'decorations'])) {
            backfillChildren(map, ['captions', 'decorations'], true);
        }
    });
}

export const migrationTo9_243 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            const sceneGroups = map.get('sceneGroups') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (sceneGroups) {
                sceneGroups.forEach((sg, sgIdx) => {
                    map.setIn(['sceneGroups', sgIdx], migrateSceneGroupCaptions(sg));
                });
            }

            const slides = map.get('slides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (slides) {
                slides.forEach((scene, sceneIdx) => {
                    map.setIn(['slides', sceneIdx], this.migrateScene(scene));
                });
            }
        });
    },

    migrateScene(scene, _isSceneDesign) {
        return scene.withMutations((map) => {
            backfillChildren(map, ['children'], true);
            backfillChildren(map, ['stickers'], true);

            const subslides = map.get('subslides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (subslides) {
                subslides.forEach((_subslide, subIdx) => {
                    backfillChildren(map, ['subslides', subIdx, 'children'], true);
                });
            }

            const presets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<unknown>>
                | undefined;
            if (presets) {
                presets.forEach((_assets, position) => {
                    backfillChildren(map, ['textPositionPresets', position], false);
                });
            }
        });
    },
});
